import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50,
  labelMono, bodyFont,
} from '../../theme/tokens2';

import { apiFetch, ApiError, friendlyMessage } from '../../api/apiClient';
import { getToken, setToken, isTokenValid } from '../../auth/token';
import { safeNextPath } from '../../auth/AuthGuard';
import { normalisePhone, phoneError } from '../../shared/phone';
import PhoneInput from '../../shared/components/PhoneInput';
import { InlineAlert } from '../../shared/components/status';

// ─── Design tokens (mirrored from Homepage / Header) ─────────────────────────
const gridBg = {
  backgroundImage: `
    linear-gradient(rgba(0,0,0,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.07) 1px, transparent 1px)
  `,
  backgroundSize: '80px 80px',
};

const KEYFRAMES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

// Messages shown when the person was sent here from a protected page
// (see AuthGuard.jsx and apiClient.js). Neutral, not red: it isn't their fault.
const REASON_MESSAGES = {
  signin:  'Please sign in to continue.',
  expired: 'Your session has expired. Please sign in again.',
};

// ─── Shared input style ───────────────────────────────────────────────────────
const inputSx = (focused) => ({
  width: '100%',
  border: `1px solid ${focused ? GREEN : BORDER}`,
  outline: 'none',
  borderRadius: 0,
  padding: '12px 14px',
  fontSize: '0.9rem',
  ...bodyFont,
  color: CHARCOAL,
  backgroundColor: '#fff',
  transition: 'border-color 0.2s ease',
  '&::placeholder': { color: MUTED_FAINT },
});

// ─── Left panel ──────────────────────────────────────────────────────────────
function LeftPanel() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '50%',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#fff',
        ...gridBg,
        opacity: 0,
        animation: 'fadeIn 0.6s ease 0.1s forwards',
      }}
    >
      {/* Corner mark — top left */}
      <Box aria-hidden sx={{ position: 'absolute', top: 28, left: 28 }}>
        <Box sx={{ width: 14, height: '1px', backgroundColor: GREEN }} />
        <Box sx={{ width: '1px', height: 14, backgroundColor: GREEN }} />
      </Box>

      {/* Corner mark — bottom right */}
      <Box aria-hidden sx={{ position: 'absolute', bottom: 28, right: 28, transform: 'rotate(180deg)' }}>
        <Box sx={{ width: 14, height: '1px', backgroundColor: GREEN }} />
        <Box sx={{ width: '1px', height: 14, backgroundColor: GREEN }} />
      </Box>

      {/* Wordmark — links back to homepage */}
      <Box sx={{ px: 6, pt: 5 }}>
        <Box
          component="button"
          onClick={() => navigate('/')}
          sx={{
            ...labelMono,
            color: CHARCOAL,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            transition: 'color 0.2s ease',
            '&:hover': { color: GREEN },
          }}
        >
          Savvy Renovator
        </Box>
      </Box>

      {/* Headline */}
      <Box sx={{ px: 10, py: 8 }}>
        <Typography
          component="h2"
          sx={{
            fontSize: 'clamp(2.8rem, 4vw, 4.4rem)',
            fontWeight: 300,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            color: CHARCOAL,
            ...bodyFont,
            opacity: 0,
            animation: 'fadeInUp 0.8s cubic-bezier(0.22,1,0.36,1) 0.4s forwards',
          }}
        >
          Plan your renovation<br />with{' '}
          <Box component="span" sx={{ fontStyle: 'italic', color: GREEN }}>
            clarity.
          </Box>
        </Typography>

        <Typography
          sx={{
            mt: 4,
            fontSize: '0.95rem',
            color: MUTED,
            lineHeight: 1.7,
            maxWidth: 340,
            ...bodyFont,
            opacity: 0,
            animation: 'fadeInUp 0.7s ease 0.65s forwards',
          }}
        >
          Your personalised renovation dashboard — layouts, materials, and quotes in one place.
        </Typography>
      </Box>

      {/* Copyright */}
      <Box sx={{ px: 6, pb: 5 }}>
        <Typography sx={{ ...labelMono, color: MUTED_FAINT }}>
          © {new Date().getFullYear()} Savvy Renovator
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Signin ───────────────────────────────────────────────────────────────────
export default function Signin() {
  const [phoneNumber,  setPhoneNumber]  = useState('');
  const [password,     setPassword]     = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [passFocused,  setPassFocused]  = useState(false);
  const [btnHovered,   setBtnHovered]   = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = safeNextPath(searchParams.get('next'), '/dashboard');
  const [reasonMessage, setReasonMessage] = useState(REASON_MESSAGES[searchParams.get('reason')] || '');

  useEffect(() => {
    const tag = document.createElement('style');
    tag.textContent = KEYFRAMES;
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  // Already signed in? Go straight to the dashboard.
  useEffect(() => {
    if (isTokenValid(getToken())) navigate(next, { replace: true });
  }, [navigate, next]);

  const canSubmit = phoneNumber.trim() !== '' && password !== '' && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setReasonMessage('');

    const phoneProblem = phoneError(phoneNumber);
    if (phoneProblem) {
      setErrorMessage(phoneProblem);
      return;
    }
    if (!password) {
      setErrorMessage('Please enter your password.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await apiFetch('/api/signin', {
        method: 'POST',
        auth: false,
        body: { phoneNumber: normalisePhone(phoneNumber), password },
      });
      setToken(data.token);
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setErrorMessage("That phone number and password don't match. Please check them and try again.");
      } else {
        setErrorMessage(friendlyMessage(err, "We couldn't sign you in. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        overflowX: 'hidden',
        ...bodyFont,
        backgroundColor: '#fff',
      }}
    >
      {/* ── Left: brand panel ── */}
      <LeftPanel />

      {/* ── Right: form panel ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: STONE50,
          borderLeft: { lg: `1px solid ${BORDER}` },
          overflowX: 'hidden',
          overflowY: 'auto',
          opacity: 0,
          animation: 'fadeIn 0.5s ease 0.2s forwards',
        }}
      >
        {/* Mobile-only wordmark + back link */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            pt: 4,
            pb: 2,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Typography sx={{ ...labelMono, color: CHARCOAL }}>
            Savvy Renovator
          </Typography>
          <Box
            component="button"
            onClick={() => navigate('/')}
            sx={{
              ...labelMono,
              color: MUTED,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              transition: 'color 0.2s ease',
              '&:hover': { color: CHARCOAL },
            }}
          >
            ← Home
          </Box>
        </Box>

        {/* Form area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', md: 'center' },
            px: { xs: 3, sm: 6, lg: 8 },
            py: { xs: 5, lg: 0 },
            maxWidth: 480,
            width: '100%',
            mx: 'auto',
          }}
        >
          {/* Heading */}
          <Typography
            component="h1"
            sx={{
              fontSize: 'clamp(1.8rem, 3vw, 2.4rem)',
              fontWeight: 300,
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              color: CHARCOAL,
              mb: 1,
            }}
          >
            Welcome back.
          </Typography>

          {/* Desktop notice */}
          <Typography
            sx={{
              mt: 1.5,
              mb: 4,
              fontSize: '0.78rem',
              color: MUTED_FAINT,
              letterSpacing: '0.02em',
              ...bodyFont,
            }}
          >
            For the best experience, the dashboard is optimised for laptop and desktop screens.
          </Typography>

          {/* Why you're here (from a protected page) */}
          {reasonMessage && (
            <InlineAlert severity="info" sx={{ mb: 3 }}>{reasonMessage}</InlineAlert>
          )}

          {/* Error message */}
          {errorMessage && (
            <InlineAlert severity="error" sx={{ mb: 3 }}>{errorMessage}</InlineAlert>
          )}

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {/* Phone field */}
            <Box>
              <Typography component="label" htmlFor="signin-phone" sx={{ ...labelMono, color: MUTED, mb: 1, display: 'block' }}>
                Phone Number
              </Typography>
              <PhoneInput
                id="signin-phone"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />          
            </Box>

            {/* Password field */}
            <Box>
              <Typography component="label" htmlFor="signin-password" sx={{ ...labelMono, color: MUTED, mb: 1, display: 'block' }}>
                Password
              </Typography>
              <Box
                component="input"
                id="signin-password"
                type="password"
                autoComplete="current-password"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPassFocused(true)}
                onBlur={() => setPassFocused(false)}
                sx={inputSx(passFocused)}
              />
            </Box>

            {/* Submit button */}
            <Box
              component="button"
              type="submit"
              disabled={!canSubmit}
              onMouseEnter={() => setBtnHovered(true)}
              onMouseLeave={() => setBtnHovered(false)}
              sx={{
                mt: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                backgroundColor: !canSubmit
                  ? 'rgba(33,131,51,0.35)'
                  : btnHovered
                  ? GREEN_HOVER
                  : GREEN,
                color: '#fff',
                border: 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                padding: '14px 32px',
                fontSize: '0.875rem',
                fontWeight: 500,
                letterSpacing: '0.05em',
                ...bodyFont,
                transition: 'background-color 0.2s ease',
                borderRadius: 0,
              }}
            >
              {submitting ? 'Signing in…' : 'Log In'}
              {!submitting && (
                <ArrowForwardIcon
                  sx={{
                    fontSize: '0.95rem !important',
                    transition: 'transform 0.2s ease',
                    transform: btnHovered && canSubmit ? 'translateX(4px)' : 'translateX(0)',
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
